/**
 * Debugging utility for beacon performance analysis
 */

// Debug state
let debugEnabled = false;
let debugLogs = [];

/**
 * Enable or disable debugging
 * @param {boolean} enabled - Whether to enable debugging
 */
export const setDebugEnabled = (enabled) => {
  debugEnabled = enabled;
  if (enabled) {
    console.log('Beacon debugging enabled');
  } else {
    console.log('Beacon debugging disabled');
  }
};

/**
 * Log debug message
 * @param {string} message - Debug message
 * @param {any} data - Additional data to log
 */
export const debugLog = (message, data = null) => {
  if (!debugEnabled) return;
  
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    message,
    data
  };
  
  debugLogs.push(logEntry);
  console.log(`[BEACON DEBUG] ${timestamp}: ${message}`, data);
};

/**
 * Get debug logs
 * @returns {Array} - Array of debug logs
 */
export const getDebugLogs = () => {
  return [...debugLogs];
};

/**
 * Clear debug logs
 */
export const clearDebugLogs = () => {
  debugLogs = [];
};

/**
 * Log element search results
 * @param {string} selector - CSS selector used
 * @param {Element|null} element - Found element or null
 */
export const logElementSearch = (selector, element) => {
  if (!debugEnabled) return;
  
  debugLog(`Element search: ${selector}`, {
    found: !!element,
    tagName: element ? element.tagName : null,
    className: element ? element.className : null,
    id: element ? element.id : null
  });
};

/**
 * Log positioning attempt
 * @param {string} targetType - Type of element being positioned on
 * @param {Object} position - Calculated position
 * @param {boolean} success - Whether positioning was successful
 */
export const logPositioningAttempt = (targetType, position, success) => {
  if (!debugEnabled) return;
  
  debugLog(`Positioning attempt: ${targetType}`, {
    position,
    success
  });
};

/**
 * Log beacon visibility change
 * @param {string} beaconType - Type of beacon
 * @param {boolean} visible - Whether beacon is visible
 * @param {Object} conditions - Conditions that affect visibility
 */
export const logBeaconVisibility = (beaconType, visible, conditions) => {
  if (!debugEnabled) return;
  
  debugLog(`Beacon visibility: ${beaconType}`, {
    visible,
    conditions
  });
};

/**
 * Performance analysis report
 */
export const generatePerformanceReport = () => {
  if (!debugEnabled) return;
  
  debugLog('Performance Analysis Report', {
    totalLogs: debugLogs.length,
    beaconVisibilityChanges: debugLogs.filter(log => log.message.includes('Beacon visibility')).length,
    positioningAttempts: debugLogs.filter(log => log.message.includes('Positioning attempt')).length,
    elementSearches: debugLogs.filter(log => log.message.includes('Element search')).length
  });
};

export default {
  setDebugEnabled,
  debugLog,
  getDebugLogs,
  clearDebugLogs,
  logElementSearch,
  logPositioningAttempt,
  logBeaconVisibility,
  generatePerformanceReport
};